// PAY2-S9 (docs/build-briefs/PAY2-S9-royalty-one-view.md): draws the one monthly royalty table. Shared by the franchisor
// console's Royalty section and the location's own Reports. Text goes in with textContent only. Every cell carries a testid
// built from the prefix, so each page tests its own table. The rows and the totals are the model's; this file only draws.

import {
  MONTHLY_COLUMNS,
  balanceOrNa,
  canResolveRow,
  debitLine,
  monthlyTotals,
  moneyOrNa,
  type MonthlyRow,
} from "./royaltyMonthlyModel.ts";
import type { Tone } from "./royaltyChargesModel.ts";

export interface MonthlyTableOptions {
  /** The testid prefix, "fr-monthly" on the console and "rm" on the location's Reports. */
  prefix: string;
  /** The franchisor view names the franchisee under the location; the location's own view does not. */
  showFranchisee: boolean;
  /** The totals row is the franchisor's; the location's own view is one row. */
  showTotals: boolean;
  /** The session holds network.royalty.manage. Resolve is offered on an open dispute only with it. */
  canResolve: boolean;
  onResolve: (row: MonthlyRow) => void;
  /** The session holds admin.payments.onboard. A row whose charge can be disputed offers Dispute. */
  canDispute: boolean;
  onDispute: (row: MonthlyRow) => void;
}

/** Text columns read from the left; the money columns are right aligned by the page's table rules. */
const LEFT_COLUMNS: readonly string[] = ["Location", "Rate", "Collection", "Dispute"];

function cell(tag: "td" | "th", text: string, testId: string, className?: string): HTMLTableCellElement {
  const node = document.createElement(tag);
  node.textContent = text;
  node.setAttribute("data-testid", testId);
  if (className !== undefined) node.className = className;
  return node;
}

function chip(text: string, tone: Tone, testId: string): HTMLElement {
  const made = document.createElement("span");
  made.className = `so-badge so-badge-${tone}`;
  made.setAttribute("data-testid", testId);
  const dot = document.createElement("span");
  dot.className = "so-badge-dot";
  dot.setAttribute("aria-hidden", "true");
  made.append(dot, text);
  return made;
}

function action(text: string, testId: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn-secondary rm-action";
  button.textContent = text;
  button.setAttribute("data-testid", testId);
  button.addEventListener("click", onClick);
  return button;
}

/** Rebuilds the table's head, body and (when asked) totals row from the merged rows. */
export function renderMonthlyTable(
  table: HTMLTableElement,
  rows: readonly MonthlyRow[],
  options: MonthlyTableOptions,
): void {
  const { prefix } = options;
  table.replaceChildren();
  const columns = MONTHLY_COLUMNS;
  const head = document.createElement("thead");
  const headRow = document.createElement("tr");
  for (const label of columns) {
    const th = document.createElement("th");
    th.scope = "col";
    if (LEFT_COLUMNS.includes(label)) th.className = "l";
    th.textContent = label;
    headRow.append(th);
  }
  head.append(headRow);

  const body = document.createElement("tbody");
  for (const row of rows) {
    const tr = document.createElement("tr");
    tr.setAttribute("data-testid", `${prefix}-row`);
    // The location's name, and on the franchisor view the franchisee's name under it: one column, so the row fits the card.
    const place = document.createElement("th");
    place.scope = "row";
    place.className = "rm-place l";
    const name = document.createElement("div");
    name.setAttribute("data-testid", `${prefix}-location`);
    name.textContent = row.location;
    place.append(name);
    if (options.showFranchisee) {
      const owner = document.createElement("div");
      owner.className = "rm-owner";
      owner.setAttribute("data-testid", `${prefix}-franchisee`);
      owner.textContent = row.franchisee;
      place.append(owner);
    }
    tr.append(place);
    tr.append(cell("td", moneyOrNa(row.netCents), `${prefix}-net`, "rm-num"));

    const rate = document.createElement("td");
    rate.setAttribute("data-testid", `${prefix}-rate`);
    rate.className = "rm-rate l";
    for (const line of row.rateLines) {
      const div = document.createElement("div");
      div.className = "rm-rate-line";
      div.setAttribute("data-testid", `${prefix}-rate-line`);
      div.textContent = line;
      rate.append(div);
    }
    tr.append(rate);

    // FRADFUND1: the fund fee is its own cell with its rate under it, never added into the royalty cell.
    const fund = cell("td", moneyOrNa(row.fundDueCents), `${prefix}-fund`, "rm-num");
    const fundRate = document.createElement("div");
    fundRate.className = "rm-owner";
    fundRate.setAttribute("data-testid", `${prefix}-fund-rate`);
    fundRate.textContent = row.fundRateText;
    fund.append(fundRate);
    tr.append(
      cell("td", moneyOrNa(row.royaltyDueCents), `${prefix}-royalty`, "rm-num"),
      fund,
      cell("td", balanceOrNa(row.adjustmentCents), `${prefix}-adjustment`, "rm-num"),
      cell("td", moneyOrNa(row.amountCents), `${prefix}-amount`, "rm-num"),
      cell("td", balanceOrNa(row.carriedCents), `${prefix}-carried`, "rm-num"),
    );

    const collection = document.createElement("td");
    collection.setAttribute("data-testid", `${prefix}-collection`);
    collection.className = "rm-collection l";
    if (row.collection === null) {
      collection.textContent = "-";
    } else {
      collection.append(chip(row.collection.label, row.collection.tone, `${prefix}-collection-chip`));
      const debit = debitLine(row.collection);
      if (debit !== null) {
        const line = document.createElement("div");
        line.className = "rm-debit";
        line.setAttribute("data-testid", `${prefix}-debit`);
        line.textContent = debit;
        collection.append(line);
      }
    }
    tr.append(collection);

    const dispute = document.createElement("td");
    dispute.setAttribute("data-testid", `${prefix}-dispute`);
    dispute.className = "rm-dispute l";
    if (row.dispute !== null) {
      dispute.append(
        chip(
          row.dispute.statusLabel,
          row.dispute.status === "open" ? "warn" : row.dispute.status === "adjusted" ? "good" : "neutral",
          `${prefix}-dispute-chip`,
        ),
      );
      if (canResolveRow(row, options.canResolve)) {
        dispute.append(action("Resolve", `${prefix}-resolve`, () => options.onResolve(row)));
      }
    } else if (row.canDispute && options.canDispute) {
      dispute.append(action("Dispute", `${prefix}-dispute-button`, () => options.onDispute(row)));
    } else {
      dispute.textContent = "-";
    }
    tr.append(dispute);
    body.append(tr);
  }
  table.append(head, body);

  if (!options.showTotals) return;
  const totals = monthlyTotals(rows);
  const foot = document.createElement("tfoot");
  const totalRow = document.createElement("tr");
  totalRow.setAttribute("data-testid", `${prefix}-totals`);
  const label = cell("th", "Total", `${prefix}-totals-label`);
  totalRow.append(
    label,
    cell("td", moneyOrNa(totals.netCents), `${prefix}-totals-net`, "rm-num"),
    cell("td", "", `${prefix}-totals-rate`),
    cell("td", moneyOrNa(totals.royaltyDueCents), `${prefix}-totals-royalty`, "rm-num"),
    cell("td", moneyOrNa(totals.fundDueCents), `${prefix}-totals-fund`, "rm-num"),
    cell("td", balanceOrNa(totals.adjustmentCents), `${prefix}-totals-adjustment`, "rm-num"),
    cell("td", moneyOrNa(totals.amountCents), `${prefix}-totals-amount`, "rm-num"),
    cell("td", balanceOrNa(totals.carriedCents), `${prefix}-totals-carried`, "rm-num"),
    cell("td", "", `${prefix}-totals-collection`),
    cell("td", "", `${prefix}-totals-dispute`),
  );
  foot.append(totalRow);
  table.append(foot);
}
